import React, { useState, useEffect } from 'react';
import { Map as CustomMap, ZoomControl } from 'react-leaflet';
import Control from 'react-leaflet-control';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { GoogleLayer } from 'react-leaflet-google-v2';
import PropTypes from 'prop-types';
import {
  Card, Row, Input, Select, Button, notification,
  Spin, message, Form,
} from 'antd';
import Geocode from 'react-geocode';
import { InfoCircleOutlined, CloseOutlined } from '@ant-design/icons';
import GreenMarkerIcon from '../../../sharedComponents/GreenMarkerIcon/GreenMarkerIcon';
import AutocompleteGoogleMaps from '../../../sharedComponents/autocompleteGoogleMaps/AutocompleteGoogleMaps';
import {
  get, post, isSuperAgentError,
  ResponseWrapper, RequestWrapper,
} from '../../../../dataSource/requests';
import { getUserType } from '../../../../utils/users';
import { MAP_API_KEY } from '../../../../utils/constants';
import './NewLand.scss';
import { backendResponseCodes } from '../../../../utils/responseMessages';
import { namespaces } from '../../../../i18n/i18n.constants';

const NewLand = (
  props: {
    nextPage: (parameters: any) => void,
    previousPage: (parameters: any) => void,
    params: any,
  },
): JSX.Element => {
  interface LandOwner {
    id: string,
    name: string
  }

  interface MatriculationWithLandOwners {
    id: string,
    landOwners: Array<LandOwner>,
  }

  const { t } = useTranslation('namespaces');
  const [formInstance] = Form.useForm();
  const navigate = useNavigate();
  const [state, setState] = useState<boolean>(true);

  const {
    nextPage, previousPage, params,
  } = props;

  const [matriculationsByToken, setMatriculationsByToken] = useState([]);
  const [
    landOwnersByMatriculation,
    setLandOwnersByMatriculation,
  ] = useState<Array<MatriculationWithLandOwners>>([]);
  const [landOwnersSelect, setLandOwnersSelect] = useState<Array<LandOwner>>([]);
  const [scrollMap, setScrollMap] = useState(null);

  const [loadingScreen, setLoadingScreen] = useState({
    text: '',
    active: false,
  });

  const [zoom, setZoom] = useState(4.5);

  const [markerAddress, setMarkerAddress] = useState({
    city: '',
    state: '',
  });

  const [markerPosition, setMarkerPosition] = useState({
    lat: null,
    lon: null,
  });

  const [currentPosition, setCurrentPosition] = useState({
    lat: -22.011084086071396,
    lon: -47.8662568891519,
  });

  const handleLoadingScreen = (text: string, active: boolean): void => {
    setLoadingScreen({ text, active });
  };

  const position = [markerPosition.lat, markerPosition.lng];

  const onSubmit = async (values): Promise<void> => {
    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;

    if (!markerPosition.lat || !markerPosition.lng) {
      message.error('Insira a localização da propriedade no mapa');
    } else if (!markerAddress.city || !markerAddress.state) {
      message.error('Não foi possivel encontrar a cidade e estado');
    } else {
      handleLoadingScreen('Salvando...', true);

      let createdLandId = null;

      const paramsCreateLand = {
        matriculationId: values.matriculation,
        name: values.landName,
        landOwnersId: values.landOwners.map((item) => item.split('::')[1]),
        location: {
          lat: markerPosition.lat,
          lon: markerPosition.lng,
        },
        city: markerAddress.city,
        state: markerAddress.state,
      };

      try {
        const landCreateLand: RequestWrapper = post({
          url: '/land/createLand',
          params: paramsCreateLand,

          frontConfigs: {
            navigateFunction: navigate,
          },
        });

        response = await landCreateLand;
      } catch (error: unknown) {
        if (isSuperAgentError(error)) {
          responseError = error.response;
        } else {
          genericError = error as Error;
        }
      }

      switch (true) {
        case (response?.body?.code === 6006):
          // "Land registered successfully", 6006
          createdLandId = response?.body.entityId;

          notification.success({
            message: 'Sucesso!',
            description: 'Propriedade adicionada.',
          });

          nextPage({ createdLandId });
          break;

        case (responseError?.body?.code === -4001):
          // "You must be authenticated to perform this operation", -4001
          break;

        case (responseError?.body?.code === -4003):
          // "You do not have permission to perform this operation", -4003
          notification.error({
            message: 'Erro',
            description: backendResponseCodes.Error
              .General.Forbidden
              .description.pt_br,
          });
          break;

        case (responseError?.body?.code === -2002):
          // "Matriculation not found", -2002
          notification.error({
            message: 'Erro',
            description: backendResponseCodes.Error
              .Matriculation.MatriculationNotFound
              .description.pt_br,
          });
          break;

        case (responseError?.body?.code === -2003):
          // "An activated matriculation from customer should be informed", -2003
          notification.error({
            message: 'Erro',
            description: backendResponseCodes.Error
              .Matriculation
              .AnActivatedMatriculationFromCustomerShouldBeInformed
              .description.pt_br,
          });
          break;

        case (responseError?.body?.code === -6015):
          // "A valid land owner should be informed", -6015
          notification.error({
            message: 'Erro',
            description: backendResponseCodes.Error
              .Land.AValidLandOwnerShouldBeInformed
              .description.pt_br,
          });
          break;

        case (genericError?.message?.includes('has been aborted')):
          // dont show anything when request is aborted
          break;

        case (responseError?.body?.code === -4000):
        default:
          // "Something unexpected happened", -4000
          notification.error({
            message: 'Erro',
            description: 'Desculpe, houve um erro ao salvar a propriedade, por favor '
              + 'tente novamente mais tarde.',
          });
          break;
      }

      handleLoadingScreen('', false);
    }
  };

  const setMatriculationsAndLandOwners = (
    matriculations: Array<MatriculationWithLandOwners>,
  ): void => {
    const newLandOwnersSelect: Array<LandOwner> = [];

    if (matriculations.length === 1 && matriculations?.[0] != null) {
      formInstance.setFieldsValue({ matriculation: matriculations[0].id });

      if (matriculations[0].landOwners.length === 1 && matriculations[0].landOwners[0] != null) {
        const landOwnerValue = matriculations[0].landOwners[0];

        formInstance.setFieldsValue({ landOwners: [`::${landOwnerValue.id}::${landOwnerValue.name}`] });
      }

      matriculations[0].landOwners.forEach((landOwner) => newLandOwnersSelect.push(landOwner));
    }

    setLandOwnersByMatriculation(matriculations);
    setLandOwnersSelect(newLandOwnersSelect);
  };

  const handleSelectedLocation = (lat, lng): void => {
    formInstance.resetFields(['latlng']);

    setZoom(12);
    setCurrentPosition({ lat, lng });
    setMarkerPosition({
      lat: null,
      lon: null,
    });
  };

  const handleMarkerPosition = async ({ latlng }): Promise<void> => {
    const { lat, lng } = latlng;
    let response;

    Geocode.setApiKey(MAP_API_KEY);

    try {
      response = await Geocode.fromLatLng(lat, lng);
    } catch (error: unknown) {
      console.log(error);
      message.error('Erro ao marcar o local no mapa, tente novamente');
      return;
    }

    const addressComponents = response.results[0].address_components;
    const newMarkerAddress = { city: '', state: '' };

    addressComponents.forEach((address) => {
      if (address.types[0].includes('administrative_area_level_1')) {
        newMarkerAddress.state = address.short_name;
      }

      if (address.types[0].includes('administrative_area_level_2')) {
        newMarkerAddress.city = address.long_name;
      }
    });

    const latValue = lat.toString().length > 12
      ? Math.round(+lat * 100000000) / 100000000 : lat;

    const lngValue = lng.toString().length > 12
      ? Math.round(+lng * 100000000) / 100000000 : lng;

    formInstance.setFieldsValue({ latlng: `${latValue}, ${lngValue}` });

    setMarkerPosition({ lat, lng });
    setZoom(16);
    setMarkerAddress(newMarkerAddress);
  };

  const matriculationChange = (matriculation): void => {
    const newLandOwnersSelect: Array<LandOwner> = [];

    landOwnersByMatriculation.forEach((owner) => {
      if (owner.id === matriculation) {
        owner.landOwners.map((landOwner) => newLandOwnersSelect.push(landOwner));
      }
    });

    formInstance.resetFields(['landOwners']);

    setLandOwnersSelect(newLandOwnersSelect);
  };

  useEffect(() => {
    const updateWindowDimensions = (): void => {
      setScrollMap(window.innerWidth >= 900);
    };

    updateWindowDimensions();

    window.addEventListener('resize', updateWindowDimensions);

    return () => {
      window.removeEventListener('resize', updateWindowDimensions);
      setState(false);
    };
  }, []);

  useEffect(() => {
    const handleCurrentPosition = (v): void => {
      const lat = v.coords.latitude;
      const lng = v.coords.longitude;

      setCurrentPosition({ lat, lng });
      setZoom(12);
    };

    const getMatriculationsWithLands = async (): Promise<void> => {
      handleLoadingScreen('Carregando...', true);

      let response: ResponseWrapper | undefined;
      let responseError: ResponseWrapper | undefined;
      let genericError: Error | undefined;

      let response2: ResponseWrapper | undefined;
      let response2Error: ResponseWrapper | undefined;
      let genericError2: Error | undefined;

      let response3: ResponseWrapper | undefined;
      let response3Error: ResponseWrapper | undefined;
      let genericError3: Error | undefined;

      if (getUserType() === 'COOPERATIVE') {
        try {
          const findMatriculationsWithLandsReqRef: RequestWrapper = get({
            url: '/customer/findMatriculationsWithLands',

            frontConfigs: {
              navigateFunction: navigate,
            },
          });

          response = await findMatriculationsWithLandsReqRef;
        } catch (error: unknown) {
          if (isSuperAgentError(error)) {
            responseError = error.response;
          } else {
            genericError = error as Error;
          }
        }

        switch (true) {
          case (response?.body?.code === 2000):
            // "Listed customer matriculations with lands successfully", 2000
            setMatriculationsByToken(response?.body?.matriculations);
            break;

          case (responseError?.body?.code === -4001):
            // "You must be authenticated to perform this operation", -4001
            break;

          case (responseError?.body?.code === -4003):
            // "You do not have permission to perform this operation", -4003
            notification.error({
              message: 'Erro',
              description: backendResponseCodes.Error.General
                .Forbidden.description.pt_br,
            });
            break;

          case (responseError?.body?.code === -1007):
            // "User not found", -1007
            notification.error({
              message: 'Erro',
              description: backendResponseCodes.Error
                .User.UserNotFound
                .description.pt_br,
            });
            break;

          case (responseError?.body?.code === -19000):
            // "SAPTimeOut", -19000
            notification.error({
              message: t('error', { ns: namespaces.common }),
              description: t('SAPTimeOut', { ns: namespaces.error.sap }),
            });
            break;

          case (responseError?.body?.code === -19001):
            // "SAPNotFound", -19001
            notification.error({
              message: t('error', { ns: namespaces.common }),
              description: t('SAPNotFound', { ns: namespaces.error.sap }),
            });
            break;

          case (responseError?.body?.code === -19002):
            // "SAPBadRequest", -19002
            notification.error({
              message: t('error', { ns: namespaces.common }),
              description: t('SAPBadRequest', { ns: namespaces.error.sap }),
            });
            break;

          case (genericError?.message?.includes('has been aborted')):
            // dont show anything when request is aborted
            break;

          default:
            notification.error({
              message: 'Erro',
              description: 'Desculpe, houve um erro ao carregar as matrículas, '
                + 'por favor tente novamente mais tarde.',
            });
            break;
        }

        try {
          const findMatriculationsWithLandOwnersReqRef: RequestWrapper = get({
            url: '/customer/findMatriculationsWithLandOwners',

            frontConfigs: {
              navigateFunction: navigate,
            },
          });

          response2 = await findMatriculationsWithLandOwnersReqRef;
        } catch (error: unknown) {
          if (isSuperAgentError(error)) {
            response2Error = error.response;
          } else {
            genericError2 = error as Error;
          }
        }

        switch (true) {
          case (response2?.body?.code === 2001):
            // "Listed activated customer matriculations with land owners successfully", 2001
            setMatriculationsAndLandOwners(response2?.body.matriculations);
            break;

          case (response2Error?.body?.code === -4001):
            // "You must be authenticated to perform this operation", -4001
            break;

          case (response2Error?.body?.code === -4003):
            // "You do not have permission to perform this operation", -4003
            notification.error({
              message: 'Erro',
              description: backendResponseCodes.Error
                .General.Forbidden
                .description.pt_br,
            });
            break;

          case (response2Error?.body?.code === -1007):
            // "User not found", -1007
            notification.error({
              message: 'Erro',
              description: backendResponseCodes.Error
                .User.UserNotFound
                .description.pt_br,
            });
            break;

          case (genericError2?.message?.includes('has been aborted')):
            // dont show anything when request is aborted
            break;

          default:
            notification.error({
              message: 'Erro',
              description: 'Desculpe, houve um erro ao carregar as matrículas, '
                + 'por favor tente novamente mais tarde.',
            });
            break;
        }

        handleLoadingScreen('', false);
      } else {
        const matriculation = params.selectedMatriculation;

        try {
          const findMatriculationsWithLandOwners3ReqRef: RequestWrapper = get({
            url: `/admin/findMatriculationsWithLandOwners?bp=${matriculation}`,

            frontConfigs: {
              navigateFunction: navigate,
            },
          });

          response3 = await findMatriculationsWithLandOwners3ReqRef;
        } catch (error: unknown) {
          if (isSuperAgentError(error)) {
            responseError = error.response;
          } else {
            genericError = error as Error;
          }
        }

        switch (true) {
          case (response3?.body?.code === 2002):
            // "Listed matriculation with land owners successfully", 2002
            setMatriculationsAndLandOwners(response3?.body.matriculations);
            setMatriculationsByToken(response3?.body.matriculations);
            break;

          case (response3Error?.body?.code === -4001):
            // "You must be authenticated to perform this operation", -4001
            break;

          case (response3Error?.body?.code === -4003):
            // "You do not have permission to perform this operation", -4003
            notification.error({
              message: 'Erro',
              description: backendResponseCodes.Error
                .General.Forbidden
                .description.pt_br,
            });
            break;

          case (response3Error?.body?.code === -1007):
            // "User not found", -1007
            notification.error({
              message: 'Erro',
              description: backendResponseCodes.Error
                .User.UserNotFound
                .description.pt_br,
            });
            break;

          case (response3Error?.body?.code === -19000):
            // "SAP timeout", -19000
            notification.error({
              message: 'Erro',
              description: backendResponseCodes.Error
                .SAP.SAPTimeOut
                .description.pt_br,
            });
            break;

          case (response3Error?.body?.code === -2002):
            // "Matriculation not found", -2002
            notification.error({
              message: 'Erro',
              description: backendResponseCodes.Error
                .Matriculation.MatriculationNotFound
                .description.pt_br,
            });
            break;

          case (genericError3?.message?.includes('has been aborted')):
            // dont show anything when request is aborted
            break;

          default:
            notification.error({
              message: 'Erro',
              description: 'Desculpe, houve um erro ao carregar as matrículas, '
                + 'por favor tente novamente mais tarde.',
            });
            break;
        }

        handleLoadingScreen('', false);
      }
    };

    window.navigator.geolocation.getCurrentPosition(
      (newPosition) => handleCurrentPosition(newPosition),
    );

    getMatriculationsWithLands();

    return () => {
      setState(false);
    };
  }, []);

  const LandForm = (
    <div className="cards">
      <Card className="info-card">
        <Row style={{ display: 'flex' }}>
          <div className="info-div">
            <InfoCircleOutlined style={{ fontSize: '22px', paddingRight: '8px' }} />
          </div>
          <div>
            Localize a propriedade sendo cadastrada e aponte a localização dela no mapa.
          </div>
        </Row>
      </Card>
      <Card className="form-inputs">
        <Form
          scrollToFirstError
          form={formInstance}
          onFinish={onSubmit}
          layout="vertical"
        >
          <Form.Item
            label="Localização"
            name="latlng"
            rules={[
              {
                required: true,
                message: 'Indique a propriedade no mapa.',
              },
            ]}
          // initialValue={handleMapLatLng}
          >
            <Input
              placeholder="00.00000000, 00.00000000"
              size="large"
              disabled
            />
          </Form.Item>

          <Form.Item
            name="matriculation"
            label="Matrícula"
            rules={[
              {
                required: true,
                message: 'Selecione a matrícula.',
              },
            ]}
          >
            <Select
              placeholder="Selecione a matrícula"
              size="large"
              onChange={matriculationChange}
              showSearch
            >
              {
                matriculationsByToken.map(
                  (v) => (
                    <Select.Option value={v.id} key={v.id}>
                      {v.id}
                    </Select.Option>
                  ),
                )
              }
            </Select>
          </Form.Item>

          <Form.Item
            label="Cooperados"
            name="landOwners"
            rules={[
              {
                required: true,
                message: 'Selecione o(s) cooperado(s).',
              },
            ]}
          >
            <Select
              mode="multiple"
              className="mat-select"
              placeholder="Digite os cooperados"
              size="large"
            >
              {
                landOwnersSelect.map(
                  (v) => (
                    <Select.Option
                      value={`::${v.id}::${v.name}`}
                      key={v.id}
                    >
                      {v.name}
                    </Select.Option>
                  ),
                )
              }
            </Select>
          </Form.Item>

          <Form.Item
            label="Nome da propriedade"
            style={{ marginBottom: '20px' }}
            name="landName"
            rules={[
              {
                required: true,
                message: 'Insira o nome da propriedade.',
              },
              {
                max: 150,
                message: 'O nome excede o tamanho máximo de 150 caracteres.',
              },
            ]}
          >
            <Input
              placeholder="Digite o nome da propriedade"
              size="large"
            />
          </Form.Item>
          <Form.Item
            noStyle
          >
            <Button
              type="save"
              htmlType="submit"
            >
              Salvar propriedade
            </Button>
          </Form.Item>
          <Button
            type="cancel"
            onClick={
              (): void => {
                previousPage();
              }
            }
          >
            <CloseOutlined />
            Cancelar
          </Button>
        </Form>
      </Card>
    </div>
  );

  return (
    <div className="NewLand">
      <Spin tip={loadingScreen.text} spinning={loadingScreen.active}>
        <CustomMap
          id="map-google"
          className="map"
          center={markerPosition.lat ? markerPosition : currentPosition}
          zoom={zoom}
          scrollWheelZoom={scrollMap}
          zoomControl={false}
          onclick={
            (eventt): void => {
              handleMarkerPosition(eventt);
            }
          }
        >
          {
            markerPosition.lat && (
              <GreenMarkerIcon
                position={position}
                popupText="Local da propriedade"
              />
            )
          }

          <ZoomControl position="bottomright" />
          <GoogleLayer googlekey={MAP_API_KEY} maptype="HYBRID" />
          <Control position="topleft" className="search-bar">
            <AutocompleteGoogleMaps
              handleSelectedLocation={handleSelectedLocation}
              handleMarkerPosition={handleMarkerPosition}
            />
          </Control>
        </CustomMap>
        {LandForm}
      </Spin>
    </div>
  );
};

NewLand.propTypes = {
  nextPage: PropTypes.func.isRequired,
  previousPage: PropTypes.func.isRequired,

  params: PropTypes.shape({
    selectedService: PropTypes.objectOf(PropTypes.any).isRequired,
    service: PropTypes.string,
    selectedMatriculation: PropTypes.string,
  }).isRequired,
};

export default NewLand;
